import { useCMSPageField } from '@/cms/fields'
import { MapAccoCard } from '@/scopes/common/components/MapAccoCard/MapAccoCard'
import { MarkerCluster } from '@/scopes/common/components/Markers/MarkerCluster'
import { MediaServerLazyImage } from '@/scopes/common/components/MediaServerLazyImage'
import { GOOGLE_MAP_ID } from '@/scopes/common/constants/GoogleMapId'
import { mapAccoMarkersSelector } from '@/scopes/destinations/domain/selectors'
import {
  useDestinationActions,
  useDestinationDependencies,
} from '@/scopes/destinations/infrastructure/depedencies/dependencies'
import { useDestinationStoreSelector } from '@/scopes/destinations/infrastructure/store/useDestinationStoreSelector'
import { useUdsToken } from '@sunwebgroup/ui-components'
import { IconButton, Sheet } from '@sunwebgroup/ui-components/uds'
import { APIProvider, Map as GoogleMap } from '@vis.gl/react-google-maps'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styles from './Map.module.css'

const DEFAULT_MAP_ZOOM_LEVEL = 8

export default function Map() {
  const intl = useIntl()
  const destinationMap = useCMSPageField('DestinationPage.map')
  const destinationId = useCMSPageField('DestinationPage.destinationId')
  const destinationType = useCMSPageField('DestinationPage.destinationType')
  const zoomLevel = useCMSPageField('DestinationPage.mapZoomLevel')
  const { googleMapsApiKey } = useDestinationDependencies()
  const { mapOpened } = useDestinationActions()
  const activeMarkerColor = useUdsToken<string>('primitives_color_secondary_300')
  const coordinates = useCMSPageField('DestinationPage.coordinates')
  const markers = useDestinationStoreSelector(mapAccoMarkersSelector)
  const [activeAccoId, setActiveAccoId] = React.useState<string | null>(null)
  const [isSheetOpen, setIsSheetOpen] = React.useState(false)

  const handleOpenMap = React.useCallback(() => {
    const nextOpen = !isSheetOpen
    if (destinationId && destinationType)
      mapOpened({ destinationId, destinationType })
    setIsSheetOpen(nextOpen)
  }, [destinationId, destinationType, mapOpened, isSheetOpen])

  return (
    <div className={styles.mapWrapper}>
      <MediaServerLazyImage
        className={styles.image}
        src={destinationMap?.imageUrl || ''}
        widths={[640, 330]}
        alt={destinationMap?.imageAltText}
        aspectRatio="1:1"
      />
      <button
        className={styles.overlayButton}
        onClick={handleOpenMap}
        aria-label={intl.formatMessage({ id: 'destination_map_open_button' })}
        type="button"
      />
      <Sheet.Root open={isSheetOpen} onOpenChange={handleOpenMap}>
        <Sheet.Trigger>
          <IconButton
            className={styles.mapZoomButton}
            variant="overlay-neutral"
            glyph="Magnifier"
            size="M"
            aria-label={intl.formatMessage({ id: 'destination_map_open_button' })}
          />
        </Sheet.Trigger>
        <Sheet.Body side="bottom" hasPadding={false}>
          <Sheet.Header
            closeLabel={intl.formatMessage({ id: 'close' })}
            titleLabel={intl.formatMessage({ id: 'destination_map_title' })}
          />
          <APIProvider apiKey={googleMapsApiKey ?? ''}>
            <GoogleMap
              mapId={GOOGLE_MAP_ID}
              defaultZoom={zoomLevel ?? DEFAULT_MAP_ZOOM_LEVEL}
              defaultCenter={!isEmpty(coordinates) ? coordinates : { lat: 0, lng: 0 }}
              reuseMaps
              fullscreenControl={false}
              headingInteractionEnabled={false}
              clickableIcons={false}
              onClick={() => setActiveAccoId(null)}
            >
              <MarkerCluster
                markersProps={markers.map(props => ({
                  ...props,
                  color: activeMarkerColor,
                  onClick: () => {
                    setActiveAccoId(props.id)
                  },
                }))}
              />
            </GoogleMap>
          </APIProvider>
          {activeAccoId && <div className={styles.mapDetails}><MapAccoCard accoId={activeAccoId} /></div>}
        </Sheet.Body>
      </Sheet.Root>
    </div>
  )
}
